'use client';

import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ExistingPlayerSearch } from '@/components/tournament/details-page/participants/ExistingPlayerSearch';
import {
  makeOrganizedMatchSchema,
  MIN_TURN_DURATION_MINUTES,
  ORGANIZED_MATCH_NOTES_MAX,
  type OrganizedMatchFormData,
} from '@/schemas/organizedMatchSchema';
import type { Sport } from '@/types/sports.model';
import {
  COMPETITIVE_HELP,
  defaultPlayersPerTeam,
  freeCourtsHint,
  isFootball,
  maxQuantity,
  organizedMatchFeeNotice,
  playersPerTeamOptions,
  type OrganizedMatchFormValues,
  type ScheduleValues,
} from './organized-match-form-model';

const GENDER_OPTIONS = [
  { value: 'ANY', label: 'Libre' },
  { value: 'MALE', label: 'Masculino' },
  { value: 'FEMALE', label: 'Femenino' },
  { value: 'MIXED', label: 'Mixto' },
];

const formatLabel = (sport: Sport | undefined, n: number): string => {
  if (isFootball(sport)) return `Fútbol ${n}`;
  return n === 1 ? 'Singles' : 'Dobles';
};

interface OrganizedMatchFormProps {
  mode: 'create' | 'edit';
  sports: Sport[];
  initial: OrganizedMatchFormValues;
  /** Edit mode only: the schedule is editable there. */
  initialSchedule?: ScheduleValues;
  /** Edit mode with players: sport, format, type and gender are locked. */
  lockedFields?: boolean;
  /** Create mode only: courts still free in the slot by sport uid, the cap of the batch. */
  freeCourts?: Record<string, number>;
  fee: number | null;
  /** Club-local "yyyy-MM-ddTHH:mm"; edit mode only, to reject a start in the past. */
  now?: string;
  submitting: boolean;
  /** Signed-in organizer: excluded from the player search (they would not show on the grid). */
  currentUserEmail?: string;
  onSubmit: (values: OrganizedMatchFormValues, schedule: ScheduleValues | undefined) => void;
}

export function OrganizedMatchForm({
  mode,
  sports,
  initial,
  initialSchedule,
  lockedFields = false,
  freeCourts,
  fee,
  now,
  submitting,
  currentUserEmail,
  onSubmit,
}: OrganizedMatchFormProps) {
  const [query, setQuery] = useState('');
  const schema = useMemo(() => makeOrganizedMatchSchema(now), [now]);
  const {
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<OrganizedMatchFormData>({
    resolver: zodResolver(schema),
    defaultValues: { ...initial, schedule: initialSchedule },
  });
  const [sportUid, playersPerTeam, matchType, players] = watch([
    'sportUid',
    'playersPerTeam',
    'matchType',
    'players',
  ]);

  const sport = sports.find((s) => s.uid === sportUid);
  const options = playersPerTeamOptions(sport);
  const quantityCap = maxQuantity(freeCourts, sportUid);
  const maxPlayers = playersPerTeam * 2;
  const reserved = useMemo(
    () =>
      new Set([
        ...players.map((p) => p.email.toLowerCase()),
        ...(currentUserEmail ? [currentUserEmail.toLowerCase()] : []),
      ]),
    [players, currentUserEmail],
  );

  // Shrinking the format may leave more players than seats.
  const resize = (nextPlayersPerTeam: number) => {
    setValue('playersPerTeam', nextPlayersPerTeam);
    setValue('players', players.slice(0, nextPlayersPerTeam * 2));
  };

  const changeSport = (uid: string) => {
    const next = sports.find((s) => s.uid === uid);
    setValue('sportUid', uid);
    setValue('quantity', Math.min(getValues('quantity'), maxQuantity(freeCourts, uid)));
    resize(
      playersPerTeamOptions(next).includes(playersPerTeam)
        ? playersPerTeam
        : defaultPlayersPerTeam(next),
    );
  };

  const scheduleError = errors.schedule?.date?.message ?? errors.schedule?.durationMinutes?.message;

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={handleSubmit(({ schedule, ...values }) => onSubmit(values, schedule))}
    >
      {mode === 'edit' && initialSchedule && (
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-1">
            <Label htmlFor="turno-date">Fecha</Label>
            <Input
              id="turno-date"
              type="date"
              min={now?.slice(0, 10)}
              {...register('schedule.date')}
              required
            />
          </div>
          <div>
            <Label htmlFor="turno-time">Hora</Label>
            <Input id="turno-time" type="time" {...register('schedule.time')} required />
          </div>
          <div>
            <Label htmlFor="turno-duration">Duración (min)</Label>
            <Input
              id="turno-duration"
              type="number"
              min={MIN_TURN_DURATION_MINUTES}
              step={30}
              {...register('schedule.durationMinutes', { valueAsNumber: true })}
              required
            />
          </div>
          {scheduleError && (
            <p role="alert" className="col-span-3 text-xs text-destructive">
              {scheduleError}
            </p>
          )}
        </div>
      )}

      <div>
        <Label>Deporte</Label>
        <Select value={sportUid} onValueChange={changeSport} disabled={lockedFields}>
          <SelectTrigger aria-label="Deporte">
            <SelectValue placeholder="Elegí un deporte" />
          </SelectTrigger>
          <SelectContent>
            {sports.map((s) => (
              <SelectItem key={s.uid} value={s.uid}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Formato</Label>
        <Select
          value={String(playersPerTeam)}
          onValueChange={(v) => resize(Number(v))}
          disabled={lockedFields}
        >
          <SelectTrigger aria-label="Formato">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((n) => (
              <SelectItem key={n} value={String(n)}>
                {formatLabel(sport, n)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Tipo</Label>
        <Controller
          control={control}
          name="matchType"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange} disabled={lockedFields}>
              <SelectTrigger aria-label="Tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FRIENDLY">Amistoso</SelectItem>
                <SelectItem value="COMPETITIVE">Competitivo</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
        {matchType === 'COMPETITIVE' && (
          <p className="mt-1 text-xs text-muted-foreground">{COMPETITIVE_HELP}</p>
        )}
      </div>

      <div>
        <Label>Género</Label>
        <Controller
          control={control}
          name="gender"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange} disabled={lockedFields}>
              <SelectTrigger aria-label="Género">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GENDER_OPTIONS.map((g) => (
                  <SelectItem key={g.value} value={g.value}>
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {mode === 'create' && (
        <div className="flex flex-col gap-2">
          <ExistingPlayerSearch
            id="turno-player-search"
            label={`Jugadores ya confirmados (opcional, hasta ${maxPlayers})`}
            query={query}
            onQueryChange={setQuery}
            reservedEmails={reserved}
            onPick={(user) => {
              if (players.length >= maxPlayers) return;
              setValue('players', [...players, { email: user.email, name: user.name }]);
              setQuery('');
            }}
          />
          {players.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {players.map((p) => (
                <li
                  key={p.email}
                  className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
                >
                  {p.name || p.email}
                  <button
                    type="button"
                    aria-label={`Quitar ${p.name || p.email}`}
                    onClick={() =>
                      setValue(
                        'players',
                        players.filter((x) => x.email !== p.email),
                      )
                    }
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {players.length >= maxPlayers && (
            <p className="text-xs text-muted-foreground">El partido ya está completo.</p>
          )}
        </div>
      )}

      <div>
        <Label htmlFor="turno-notes">Notas</Label>
        <Textarea
          id="turno-notes"
          {...register('notes')}
          rows={3}
          maxLength={ORGANIZED_MATCH_NOTES_MAX}
        />
        {errors.notes?.message && (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {errors.notes.message}
          </p>
        )}
      </div>

      {mode === 'create' && (
        <div>
          <Label htmlFor="turno-quantity">Cantidad de partidos</Label>
          <Controller
            control={control}
            name="quantity"
            render={({ field }) => (
              <Input
                id="turno-quantity"
                type="number"
                min={1}
                max={quantityCap}
                disabled={players.length > 0}
                value={players.length > 0 ? 1 : field.value}
                onChange={(e) =>
                  field.onChange(Math.min(quantityCap, Math.max(1, Number(e.target.value) || 1)))
                }
              />
            )}
          />
          {freeCourts && sport && (
            <p className="mt-1 text-xs text-muted-foreground">
              {freeCourtsHint(freeCourts[sport.uid] ?? 0, sport.name)}
            </p>
          )}
          {players.length > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              Con jugadores cargados se crea un solo partido.
            </p>
          )}
        </div>
      )}

      {mode === 'create' && (
        <p className="rounded-md bg-muted p-2 text-xs text-muted-foreground">
          {organizedMatchFeeNotice(fee)}
        </p>
      )}

      <Button type="submit" disabled={submitting || !sportUid}>
        {mode === 'create' ? 'Crear partido' : 'Guardar cambios'}
      </Button>
    </form>
  );
}
